/** `/notifications/ai-agent` — the console agent (backend metaplan A9; AIE §10 "Console agent", Q6).
 *
 *  An ops colleague describes a notification change for ONE tenant; the agent works it out with
 *  the same AI-editing tools MCP clients use (read + propose only) and builds a proposal. It never
 *  applies anything: when a preview is ready it asks for apply, which puts a pending change in the
 *  review panel next to the chat — the generalized proposal review page (`ProposalReview`), with
 *  the renders on real bookings and an Apply button only a person can click
 *  (`console_notification_mcp_write`).
 *
 *  - Left: the shared AI chat panel (`features/ai-diag`), turns as background tasks, threads in
 *    `?thread=` (shareable), without the diagnose tools' evidence tab.
 *  - Right: the conversation's proposals; the one the latest answer touched is selected.
 *  - Debug mode (X-CP-Debug) adds each answer's tool calls (tool, outcome, duration, arguments). */
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Alert, Box, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { aiEditKeys } from '@/api/aiEdit';
import {
  CONSOLE_AGENT_TASK_TYPE,
  consoleAgentKeys,
  launchContext,
  startTurn,
  tenantLabelToken,
  type ConsoleAgentLaunchContext,
  type ConsoleAgentTurnResult,
} from '@/api/consoleAgent';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/ReadView';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { ProposalReviewById } from '@/features/ai-changes/AIProposalReviewPage';
import { AiDiagChat } from '@/features/ai-diag/AiDiagChat';
import type { AiDiagAdapter, DiagnoseLaunchContext, DiagnoseResult } from '@/features/ai-diag/types';
import { useDebugMode } from '@/features/debug/useDebugMode';
import { usePermissions } from '@/hooks/usePermissions';
import { usePageTenant } from '@/scope/usePageTenant';

export function AIAgentPage() {
  const pageTenant = usePageTenant();
  const { tenantId } = pageTenant;
  return (
    <Box>
      <PageHeader
        helpStory="ai-agent"
        title="AI agent"
        subtitle="Describe a notification change for one tenant. The agent proposes it; a person reviews and applies it."
      />
      <Box sx={{ width: { xs: '100%', sm: 320 }, mb: 2 }}>
        <TenantPicker value={pageTenant.value} onChange={pageTenant.setTenant} placeholder="Pick a tenant" />
      </Box>
      {tenantId == null ? (
        <Alert severity="info" icon={<SmartToyIcon />}>
          Pick the tenant whose notifications you want to change. A conversation works on one tenant
          only.
        </Alert>
      ) : (
        // A new tenant is a new workspace: the chat, its thread and the selected proposal reset.
        <AgentWorkspace key={tenantId} tenantId={tenantId} />
      )}
    </Box>
  );
}

/** The chat panel's launch context from the agent's (blockers are shown by the page). */
function toChatContext(ctx: ConsoleAgentLaunchContext): DiagnoseLaunchContext {
  return { enabled: ctx.enabled && ctx.ready, models: ctx.models, caps: ctx.caps };
}

function AgentWorkspace({ tenantId }: { tenantId: number }) {
  const queryClient = useQueryClient();
  const { hasPerm } = usePermissions();
  const debug = useDebugMode().enabled;
  const ctxQuery = useQuery({
    queryKey: consoleAgentKeys.context(tenantId),
    queryFn: () => launchContext(tenantId),
    staleTime: 60_000,
  });
  const [results, setResults] = useState<ConsoleAgentTurnResult[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);

  const onThreadResults = useCallback(
    (settled: DiagnoseResult[] | null) =>
      setResults(settled as unknown as ConsoleAgentTurnResult[] | null),
    [],
  );
  const latest = results && results.length > 0 ? results[results.length - 1] : undefined;
  const proposals = useMemo(() => latest?.thread_proposals ?? [], [latest]);

  // Follow the conversation: select what the latest answer worked on, and refetch the proposals
  // it touched (new items, a fresh preview, a pending change).
  useEffect(() => {
    if (!latest) {
      setSelected(null);
      return;
    }
    for (const id of latest.proposals) {
      void queryClient.invalidateQueries({ queryKey: aiEditKeys.proposal(id) });
    }
    const touched = latest.proposals[latest.proposals.length - 1];
    setSelected((current) =>
      touched !== undefined
        ? touched
        : current !== null && latest.thread_proposals.includes(current)
          ? current
          : (latest.thread_proposals[latest.thread_proposals.length - 1] ?? null),
    );
  }, [latest, queryClient]);

  const ctx = ctxQuery.data;
  const adapter: AiDiagAdapter = useMemo(
    () => ({
      launchContext: async () =>
        toChatContext(
          await queryClient.ensureQueryData({
            queryKey: consoleAgentKeys.context(tenantId),
            queryFn: () => launchContext(tenantId),
          }),
        ),
      contextKey: String(tenantId),
      start: (body) =>
        startTurn(tenantId, {
          question: body.question,
          ...(body.model ? { model: body.model } : {}),
          ...(body.thread_task_id ? { thread_task_id: body.thread_task_id } : {}),
          ...(body.budget_usd ? { budget_usd: body.budget_usd } : {}),
        }),
      taskType: CONSOLE_AGENT_TASK_TYPE,
      subjectNoun: 'tenant',
      subjectTitle: `AI agent — ${ctx?.tenant.code ?? `tenant ${tenantId}`}`,
      questionPlaceholder:
        'e.g. "Use the new meeting-point photo for Paris tours departing 13–31 October" or ' +
        '"Add Portuguese to the booking confirmation"',
      labelContains: tenantLabelToken(tenantId),
      matchesSubject: (params) =>
        (params as { tour_operator_id?: number } | null)?.tour_operator_id === tenantId,
      tenantId,
      evidence: false,
      copy: {
        title: 'Ask the agent',
        threadTitle: 'Conversation',
        questionLabel: 'What should change?',
        followUpLabel: 'Reply',
        submit: 'Send',
        submitFollowUp: 'Send',
        running: 'Working…',
        runningHint: '(a preview can take a minute or two)',
        notEnabled: 'The agent can’t run on this tenant now — see above.',
        linkNoun: 'conversation',
        runNoun: 'turn',
        threadGone: 'That conversation no longer exists.',
        otherSubject: 'That conversation is about a different tenant.',
      },
      renderAnswerFooter: (result) => (
        <AnswerFooter
          result={result as unknown as ConsoleAgentTurnResult}
          debug={debug}
          selected={selected}
          onSelect={setSelected}
        />
      ),
      onThreadResults,
    }),
    [tenantId, ctx?.tenant.code, debug, selected, onThreadResults, queryClient],
  );

  if (ctxQuery.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (ctxQuery.isError || !ctx) {
    return <Alert severity="error">Couldn’t load the agent for this tenant.</Alert>;
  }
  return (
    <Stack spacing={2}>
      <Notices ctx={ctx} />
      {ctx.ready && ctx.enabled && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <AiDiagChat
            adapter={adapter}
            subjectNote={`The agent works on ${ctx.tenant.code} only, with the AI-editing tools: it reads and proposes, it never applies.`}
          />
          <ReviewPanel
            proposals={proposals}
            selected={selected}
            onSelect={setSelected}
            canView={hasPerm('console_ai_changes_view')}
            canApply={ctx.can_apply}
          />
        </Box>
      )}
    </Stack>
  );
}

function Notices({ ctx }: { ctx: ConsoleAgentLaunchContext }) {
  return (
    <>
      {!ctx.enabled && <Alert severity="info">The console agent is switched off on this deployment.</Alert>}
      {ctx.blockers.map((b) => (
        <Alert key={b.code} severity="warning" data-agent-blocker={b.code}>
          {b.message}
          {b.code === 'tenant_not_opted_in' && (
            <>
              {' '}
              <Link component={RouterLink} to={`/tour-operators/${ctx.tenant.id}/ai-editing`}>
                Open the tenant’s AI editing tab
              </Link>
            </>
          )}
        </Alert>
      ))}
      {ctx.ready &&
        ctx.notes.map((n) => (
          <Alert key={n.code} severity="info" data-agent-note={n.code}>
            {n.message}
          </Alert>
        ))}
    </>
  );
}

function AnswerFooter({
  result,
  debug,
  selected,
  onSelect,
}: {
  result: ConsoleAgentTurnResult;
  debug: boolean;
  selected: number | null;
  onSelect: (id: number) => void;
}) {
  const proposals = result.proposals ?? [];
  const calls = result.calls ?? [];
  if (proposals.length === 0 && !(debug && calls.length > 0)) return null;
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {proposals.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary">
            Worked on
          </Typography>
          {proposals.map((id) => (
            <Chip
              key={id}
              size="small"
              label={`Proposal #${id}`}
              color={selected === id ? 'primary' : 'default'}
              variant={selected === id ? 'filled' : 'outlined'}
              onClick={() => onSelect(id)}
              data-answer-proposal={id}
            />
          ))}
        </Stack>
      )}
      {debug && calls.length > 0 && (
        <Box
          data-agent-trace
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'action.hover' }}
        >
          <Typography variant="caption" color="text.secondary">
            Tool calls ({calls.length}) — debug mode
          </Typography>
          {calls.map((call, i) => (
            <Typography
              key={`${i}-${call.tool}`}
              variant="caption"
              component="div"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
            >
              {call.ok ? '✓' : '✗'} {call.tool} · {call.code} · {call.duration_ms} ms ·{' '}
              {JSON.stringify(call.input)}
            </Typography>
          ))}
        </Box>
      )}
    </Stack>
  );
}

function ReviewPanel({
  proposals,
  selected,
  onSelect,
  canView,
  canApply,
}: {
  proposals: number[];
  selected: number | null;
  onSelect: (id: number) => void;
  canView: boolean;
  canApply: boolean;
}) {
  if (proposals.length === 0 || selected === null) {
    return (
      <SectionCard title="Review">
        <Typography variant="body2" color="text.secondary">
          The proposals the agent builds show here. When one is previewed and you want it, the agent
          asks for apply: check the change — including the renders on real bookings — and click
          Apply. Nothing changes before that.
        </Typography>
        {!canApply && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Applying needs console_notification_mcp_write (and change sets switched on).
          </Typography>
        )}
      </SectionCard>
    );
  }
  return (
    <Box data-review-panel>
      {proposals.length > 1 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {proposals.map((id) => (
            <Chip
              key={id}
              size="small"
              label={`#${id}`}
              color={selected === id ? 'primary' : 'default'}
              variant={selected === id ? 'filled' : 'outlined'}
              onClick={() => onSelect(id)}
            />
          ))}
        </Stack>
      )}
      {canView ? (
        <ProposalReviewById id={selected} embedded />
      ) : (
        <Alert severity="info">Reviewing proposals needs the console_ai_changes_view permission.</Alert>
      )}
    </Box>
  );
}
